import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { initializeConsoleMonitoring } from './utils/qaHealth';
import { logger } from './utils/logger';
import './styles/index.css';
import './styles/buttons.css';
import { bootstrapHostTheme } from './config/themes';
import { clientTabTitleForHost } from './config/clientBrand';
import { initBlissFunnelAnalytics } from './services/funnelAnalytics';
import { BLISS_BLACK, BLISS_CREAM, BLISS_INK, isBlissClientLightSurface } from './config/blissPalette';
import { isStaleDeployErrorMessage, reloadOnceForStaleDeploy } from './utils/staleDeployReload';

// Host theme before first paint (buttons.css :root defaults to Realtimax slate until bootstrap).
bootstrapHostTheme();

// Tab title and favicon per host, before React mounts. The tab is a
// client-facing surface, so an unrecognized host takes the client brand
// instead of the internal platform name.
function applyBrandMeta() {
  const host = window.location.hostname.toLowerCase();
  if (host.includes('realtimax')) {
    document.title = 'Realtimax';
    const existing = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (existing) {
      existing.href = '/realtimax-icon.svg';
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = '/realtimax-icon.svg';
      document.head.appendChild(link);
    }
    return;
  }
  const tenantSeo = document.querySelector<HTMLMetaElement>('meta[name="tenant-seo-hosts"]');
  if (!(tenantSeo?.content.split(',').includes(host))) {
    document.title = clientTabTitleForHost(host);
  }
}
applyBrandMeta();
// Exact tenant hosts receive the verified main-site GA4 ID at build time.
const tenantAnalyticsId = document.querySelector<HTMLMetaElement>('meta[name="tenant-ga4-measurement-id"]')?.content;
const analyticsHosts = document.querySelector<HTMLMetaElement>('meta[name="tenant-seo-hosts"]')?.content.split(',') || [];
if (analyticsHosts.includes(window.location.hostname.toLowerCase())) {
  initBlissFunnelAnalytics({ ga4Id: tenantAnalyticsId || null });
}

const log = logger.module('Main');

// =============================================================================
// STARTUP VALIDATION: DEPRECATED - Supabase is now source of truth
// localStorage repair no longer needed since data comes from cloud
// =============================================================================
function validateAndRepairLocalStorage(): void {
  // DEPRECATED: Supabase is source of truth - no localStorage validation needed
  console.log('[Startup] Supabase is source of truth - localStorage validation skipped');
}

// Run validation before anything else
validateAndRepairLocalStorage();

// Initialize console error monitoring for QA health checks
initializeConsoleMonitoring();

// Initialize cloud migration tools (available via console)
import('./utils/migrateToCloud').catch(() => {
  log.debug('Cloud migration tools not available');
});

// Global error handler for uncaught errors (catches errors before React mounts)
window.onerror = (message, source, lineno, colno, error) => {
  log.error('Global error caught:', { message, source, lineno, colno, error });
  
  // Show fallback UI if React hasn't mounted
  const root = document.getElementById('root');
  if (root && !root.hasChildNodes()) {
    const host = window.location.hostname.toLowerCase();
    const blissLight = isBlissClientLightSurface(host, window.location.pathname);
    const errBg = blissLight ? BLISS_CREAM : BLISS_BLACK;
    const errText = blissLight ? BLISS_INK : '#ffffff';
    const errBtnText = blissLight ? BLISS_INK : BLISS_BLACK;
    root.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:${errBg};color:${errText};font-family:system-ui;text-align:center;padding:20px">
        <div>
          <h1 style="color:var(--color-gold);margin-bottom:16px">Application Error</h1>
          <p style="opacity:0.75;margin-bottom:16px">${message}</p>
          <p style="font-size:12px;opacity:0.45">Check console for details</p>
          <button onclick="location.reload()" style="margin-top:20px;padding:12px 24px;background:var(--color-gold);color:${errBtnText};border:none;border-radius:8px;font-weight:600;cursor:pointer">Reload Page</button>
        </div>
      </div>
    `;
  }
  return true;
};

// Recover failed Vite chunk/CSS preloads before they reach a lazy route.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleDeploy('Vite asset preload failed')) event.preventDefault();
});

// Handle unhandled promise rejections
// Detect stale JS chunk 404s (happen after a new Vercel deploy when the
// browser has cached the old HTML referencing old chunk filenames).
// Force a hard reload so the browser picks up the new bundle.
window.onunhandledrejection = (event) => {
  const msg = event.reason?.message ?? String(event.reason ?? '');
  if (isStaleDeployErrorMessage(msg)) {
    if (reloadOnceForStaleDeploy(msg)) return;
  }
  log.error('Unhandled promise rejection:', event.reason);
};

// Build status is now tracked via console logging only
// QA health checks should use Supabase or API endpoints
console.log('[BUILD] App started successfully', {
  version: '1.0.0',
  environment: import.meta.env.MODE,
  timestamp: new Date().toISOString()
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);


